import { cloneDeep, get, merge, set, cond, unset, stubTrue, isUndefined, has, find, filter } from 'lodash';
import { envValue } from '../../config/envUtils';
import { CRM_SIGN_IN_TYPE } from '../constants/crm-sign-in-type';
import { SAML_SSO_DEFAULT_VALUES } from '../constants/SamlSsoDefaultValues';
import { CRM_PROFILE_DEFAULT_VALUES } from '../constants/CrmProfileDefaultValues';
import { PRICE_DISPLAY_TAX_FEE_OPTIONS } from '../constants/PricingDisplay';
import { addOnsTypes } from '../constants/GeneralConfiguration';
import { IN_CONTEXT_BOOKING_STATUS } from '../constants/InContextBookingStatus';

export const removeArrayDuplicatesInObject = obj => {
    const result = {};
    for (const key in obj) {
        const value = obj[key];
        if (typeof value !== 'object') {
            result[key] = value;
        } else if (Array.isArray(value)) {
            result[key] = _.uniq(value);
        } else {
            result[key] = removeArrayDuplicatesInObject(value);
        }
    }
    return result;
};

export const updateConfigCrmSettingsBasedOnSignInType = (currentConfigCopy) => {
    const { NONE, PROFILE_PROVIDER, SAML_SSO } = CRM_SIGN_IN_TYPE;
    const crmSignInType = get(currentConfigCopy, 'crm.crmSignInType');

    switch (crmSignInType) {
        case NONE:
            return merge(currentConfigCopy, CRM_PROFILE_DEFAULT_VALUES, SAML_SSO_DEFAULT_VALUES);
        case PROFILE_PROVIDER:
            return merge(currentConfigCopy, SAML_SSO_DEFAULT_VALUES);
        case SAML_SSO:
            return merge(currentConfigCopy, CRM_PROFILE_DEFAULT_VALUES);
        default:
            return merge(currentConfigCopy, CRM_PROFILE_DEFAULT_VALUES, SAML_SSO_DEFAULT_VALUES);
    }
};

const sanitizeVideoPaths = (videoPaths = []) => {
    return videoPaths
        .map(videoPath => {
            if (typeof videoPath.undefined === 'string' && videoPath.undefined.length > 3)
                return { Code: videoPath.Code, Path: videoPath.undefined };
            return videoPath;
        })
        .filter(videoPath => videoPath.Code && videoPath.Path);
};

/**
* Filters out the category codes from retailProductCategoriesToDisplay config that are not present in
* retailProductCategories.
* @param {Array} retailProductCategoriesToDisplay
* @param {Array} retailProductCategories
* @returns {Array}
*/
export const sanitizeRetailProductCategoriesToDisplay = (retailProductCategoriesToDisplay = [],
    retailProductCategories = []) => {
    return filter(retailProductCategoriesToDisplay, code => !!find(retailProductCategories, { code }))
};

/**
 * Returns the showAddOns configuration that needs to be migrated.
 * showAddOns intially was either false or a number used to determine the display type of the add-ons.
 * Then showAddOns was changed to a boolean and showAddOnsDisplayType (number) was introduced to determine the display type of the add-ons.
 * Then showAddOnsDisplayType was removed and showAddOnCrossSellOnConfirmationPage (boolean) was introduced
 * along with showAddOnCrossSellInProductDetails, showAddOnCrossSellInAddedToCartOverlay, and showAddOnCrossSellOnCartPage
 * to determine the display type of the add-ons.
 *
 * @param {Object} config - The configuration object
 * @returns {Object} - Object with updated data that should overwrite the old data
 */
export const getShowAddOnsMigratedConfig = (config) => {
    const { showAddOns, showAddOnsDisplayType } = config;
    const parsedShowAddonsDisplayTypeFromShowAddons = parseInt(showAddOns);
    const confirmationCrossSellDisplayTypes = [
        addOnsTypes.BOOKING_AND_CONFIRMATION_PAGE,
        addOnsTypes.CONFIRMATION_PAGE_ONLY
    ];
    if (Number.isInteger(parsedShowAddonsDisplayTypeFromShowAddons)) {
        return {
            showAddOns: true,
            showAddOnCrossSellOnConfirmationPage: confirmationCrossSellDisplayTypes.includes(
                parsedShowAddonsDisplayTypeFromShowAddons
            )
        };
    }
    if (Number.isInteger(showAddOnsDisplayType)) {
        return {
            showAddOns:
                showAddOns &&
                [addOnsTypes.BOOKING_ONLY, addOnsTypes.BOOKING_AND_CONFIRMATION_PAGE].includes(showAddOnsDisplayType),
            showAddOnCrossSellOnConfirmationPage:
                showAddOns && confirmationCrossSellDisplayTypes.includes(showAddOnsDisplayType)
        };
    }
    return {};
};

export const updateAddOnsConfig = (config) => {
    const showAddOnsMigrationConfig = getShowAddOnsMigratedConfig(config);
    merge(config, showAddOnsMigrationConfig);
    has(config, 'showAddOnsDisplayType') && unset(config, 'showAddOnsDisplayType');
};


export const getPricingDisplayTaxAndFeeOption = (config) => {
    const { showPricingInclusiveOfTax, pricingDisplayTaxAndFeeOption } = config
    return cond([
        [() => !!pricingDisplayTaxAndFeeOption, () => pricingDisplayTaxAndFeeOption],
        [() => showPricingInclusiveOfTax,
            () => PRICE_DISPLAY_TAX_FEE_OPTIONS.INCLUDE_TAXES_FEES],
        [() => !isUndefined(showPricingInclusiveOfTax) && !showPricingInclusiveOfTax,
            () => PRICE_DISPLAY_TAX_FEE_OPTIONS.EXCLUDE_TAXES_FEES],
        [stubTrue, () => PRICE_DISPLAY_TAX_FEE_OPTIONS.INCLUDE_TAXES_FEES]])()
};

const VALID_IN_CONTEXT_BOOKING_STATUSES = new Set(Object.values(IN_CONTEXT_BOOKING_STATUS));

/**
 * Returns an override fragment for `inContextBookingStatus` to be merged on top of the loaded config.
 *
 * Backward-compatibility
 * - Field absent or explicitly undefined → treat as `Live` (existing properties remain active)
 * - Field present with valid value       → no override (return empty object)
 * - Field present with invalid value     → default to `Inactive` (safe suppression)
 *
 * @param {Object} config - The raw configuration object loaded from the API
 * @returns {Object} Override fragment to merge on top of the loaded config
 */
export const getInContextBookingStatusMigrationOverride = (config) => {
    if (!has(config, 'inContextBookingStatus') || config.inContextBookingStatus === undefined) {
        return { inContextBookingStatus: IN_CONTEXT_BOOKING_STATUS.LIVE };
    }
    if (!VALID_IN_CONTEXT_BOOKING_STATUSES.has(config.inContextBookingStatus)) {
        return { inContextBookingStatus: IN_CONTEXT_BOOKING_STATUS.INACTIVE };
    }
    return {};
};

export const sanitizeConfig = (obj, retailProductCategories) => {
    const sanitizedConfig = cloneDeep(obj);
    /* Validate videoPaths in config, to remove empty paths and codes */
    sanitizedConfig.videoPaths = sanitizeVideoPaths(obj.videoPaths);
    sanitizedConfig.retailProductCategoriesToDisplay = sanitizeRetailProductCategoriesToDisplay(
        get(obj, 'retailProductCategoriesToDisplay'), retailProductCategories)
    !get(sanitizedConfig, 'signInForBenefits.inUse') && set(sanitizedConfig, 'signInForBenefits.loyaltyAssignments', []);
    has(sanitizedConfig, 'showPricingInclusiveOfTax') && unset(sanitizedConfig, 'showPricingInclusiveOfTax')
    updateAddOnsConfig(sanitizedConfig);
    return updateConfigCrmSettingsBasedOnSignInType(sanitizedConfig);
};

export const IsTestEnv = () => process.env.NODE_ENV === 'DEV' || /elabtest[12]|t1|t2|l1|l2|t3|t4|t4b/.test(envValue('ENV_CODE'));


// WEBPACK FOOTER //
// ./src/utils/configuration-utils.js